/**
 * Data that we prefetch when site loads.
 */

import { Store } from 'redux';

import config, { Language } from '../config';
import { BRAND, CATEGORIES, CMS_ITEM_URL, COLOR, CONDITION, MATERIAL } from '../config/api-routes';
import { GET } from '../utils/request';
import { formatResponse } from '../utils/service-helpers';
import { RootState } from '.';
import { saveCategoryTree } from './actions/category.actions';
import { saveAllCmsData } from './actions/cms.actions';
import { setFilterMenuData } from './actions/filter-menu.actions';
import { StoreData } from './root-reducer';

const prefetchItems: {
  key: keyof StoreData | string;
  endpoint: string | ((language?: Language) => string);
  actionMethod: (...args: any) => any;
}[] = [
  {
    key: 'cms',
    endpoint: (language?: Language) => CMS_ITEM_URL(undefined, { language }),
    actionMethod: (cachedCmsData) => saveAllCmsData(cachedCmsData),
  },
  {
    key: 'categoryData',
    // Get minimum amount of needed data as next.js asks.
    endpoint: CATEGORIES.LIST.GET({ flatTree: true, withCategoryTitlePath: true, simplified: true, onlyCamelCased: true }),
    actionMethod: (categoryData) => saveCategoryTree(categoryData),
  },
  {
    key: 'filterMenu/CONDITIONS',
    endpoint: CONDITION.LIST.GET(),
    actionMethod: (filterMenu) => setFilterMenuData('CONDITIONS', filterMenu),
  },
  {
    key: 'filterMenu/BRANDS',
    endpoint: BRAND.LIST.GET({
      groupedFlat: true,
      sortOtherAlphabetically: true,
    }),
    actionMethod: (filterMenu) => setFilterMenuData('BRANDS', filterMenu),
  },
  {
    key: 'filterMenu/COLORS',
    endpoint: COLOR.LIST.GET(),
    actionMethod: (filterMenu) => setFilterMenuData('COLORS', filterMenu),
  },
  {
    key: 'filterMenu/MATERIALS',
    endpoint: MATERIAL.LIST.GET(),
    actionMethod: (filterMenu) => setFilterMenuData('MATERIALS', filterMenu),
  },
  {
    key: 'filterMenu/CATEGORIES',
    endpoint: CATEGORIES.LIST.GET({ flatTree: true, withCategoryTitlePath: true, simplified: true, onlyCamelCased: true }),
    actionMethod: (filterMenu) => setFilterMenuData('CATEGORIES', filterMenu),
  },
];

export const prefetchToStore = async (store: Store<RootState>) => {
  // CRITICAL FIX: Read locale from the SSR store instance (not global store!)
  // During SSR, each request has its own store instance with locale set in _app.getInitialProps
  const currentLocale = store.getState()?.commonData?.locale;
  const currentLanguage = (currentLocale?.split?.('-')?.[0] as Language) || (config.get('countryLanguage') as Language);

  return Promise.all(
    prefetchItems.map(async ({ endpoint, actionMethod }) => {
      // No frontend caching - let backend Redis cache handle it
      try {
        // Resolve endpoint if it's a function, passing the language from SSR store
        const resolvedEndpoint = typeof endpoint === 'function' ? endpoint(currentLanguage) : endpoint;

        // Pass language header explicitly to override getLanguage() which reads from global store
        const response = await GET(resolvedEndpoint, {
          headers: {
            'Cache-Control': 'no-cache',
            'x-language': currentLanguage,
          },
        });
        const data = formatResponse(response);

        if (data) {
          await store.dispatch(actionMethod(data));
        }

        return data;
      } catch (reason) {
        console.error(reason);

        return null;
      }
    }),
  );
};
